import React, { useCallback, useEffect } from 'react';
import { useDataModelerFeature } from '../../utils/hooks';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { ContentLoader } from '../../components/Loaders';
import DataModelerSchema from './DataModelerSchema';
import {
  dataModelerSelectCurrentDraftEdits,
  dataModelerSelectCurrentSchemaObj,
} from '../../redux/selectors/dataModeler';
import {
  ISchemaDataModelQuery,
  ISchemaDataModelQueryVariables,
} from '../../utils/apollo/graphqlSchema';
import {
  dataModelerSetCurrentSchemaId,
  dataModelerSetLockedTtlSeconds,
  dataModelerSetSchemaDataModel,
} from '../../redux/slices/dataModeler';
import { useQuery } from '@apollo/client';
import { DATA_MODEL_GET_SCHEMA } from '../../utils/apollo/dataModeler/queries';
import { useDataModelerPermittedSchemas } from './DataModelerSchema/utils';
import isNil from 'lodash/isNil';
import { batch } from 'react-redux';
import { useAnalytics } from '../../utils/analytics/useAnalytics';
import { pageViewed } from '../../utils/analytics/events/page/events';
import { DataModelerProvider } from './DataModelerContext';

const DataModelerPage = () => {
  const dispatch = useAppDispatch();
  const { currentSchemaId } = useDataModelerPermittedSchemas();
  const { track } = useAnalytics();
  useEffect(() => {
    track(
      pageViewed({
        category: 'data modeler',
        name: 'schema',
        id: currentSchemaId,
      }),
    );
  }, [currentSchemaId, track]);

  const stateCurrentSchemaId = useAppSelector(
    (state) => state.dataModeler.currentSchemaId,
  );
  const schemaObj = useAppSelector(dataModelerSelectCurrentSchemaObj);
  const draftEdits = useAppSelector(dataModelerSelectCurrentDraftEdits);
  const featureEnabled = useDataModelerFeature();

  const onSchemaLoad = useCallback(
    (data: ISchemaDataModelQuery) => {
      batch(() => {
        dispatch(
          dataModelerSetLockedTtlSeconds(
            data.schemaDataModel.lockedTtlSeconds ?? 0,
          ),
        );
        dispatch(
          dataModelerSetSchemaDataModel({
            newModel: data.schemaDataModel,
            setAsCurrent: true,
          }),
        );
      });
    },
    [dispatch],
  );

  const { loading } = useQuery<
    ISchemaDataModelQuery,
    ISchemaDataModelQueryVariables
  >(DATA_MODEL_GET_SCHEMA, {
    variables: {
      schemaKey: currentSchemaId ?? '',
    },
    notifyOnNetworkStatusChange: true,
    skip:
      !currentSchemaId ||
      !featureEnabled ||
      schemaObj?.key === currentSchemaId ||
      draftEdits?.isNew,
    onCompleted: onSchemaLoad,
  });

  // XXX this effect is needed to keep the redux state currentSchemaId in sync
  // with the navigated (url param) schemaId.
  // Note that dataModelerSetSchemaDataModel (on query complete) also sets that
  useEffect(() => {
    if (!stateCurrentSchemaId) {
      return;
    }
    if (currentSchemaId === stateCurrentSchemaId) {
      return;
    }
    if (!currentSchemaId) {
      return;
    }
    dispatch(dataModelerSetCurrentSchemaId({ schemaId: currentSchemaId }));
  }, [currentSchemaId, stateCurrentSchemaId, dispatch]);

  if (!featureEnabled) {
    return <>Data modeler is not enabled for this customer</>;
  }

  if (loading) {
    return <ContentLoader />;
  }

  if (!currentSchemaId) {
    return <>No schema selected</>;
  }

  if (isNil(schemaObj)) {
    return <ContentLoader />;
  }

  return (
    <DataModelerProvider>
      <DataModelerSchema />
    </DataModelerProvider>
  );
};

export default DataModelerPage;
